import { getUniqId } from '~/shared/lib'

export interface ProjectMemberStatus {
  id: string
  name: string
}

const statusesByProject = ref<Record<string, ProjectMemberStatus[]>>({})
const memberStatusIdsByProject = ref<Record<string, Record<string, string[]>>>({})

function ensureProjectStatuses(projectId: string) {
  if (!statusesByProject.value[projectId]) {
    statusesByProject.value[projectId] = []
  }
  if (!memberStatusIdsByProject.value[projectId]) {
    memberStatusIdsByProject.value[projectId] = {}
  }
}

export function useProjectMemberStatuses(projectId: MaybeRefOrGetter<string>) {
  const resolvedProjectId = computed(() => toValue(projectId))

  const statuses = computed(() => {
    const id = resolvedProjectId.value
    if (!id) {
      return []
    }
    return statusesByProject.value[id] ?? []
  })

  function getMemberStatusIds(userId: string): string[] {
    const id = resolvedProjectId.value
    if (!id) {
      return []
    }
    return memberStatusIdsByProject.value[id]?.[userId] ?? []
  }

  function getMemberStatuses(userId: string): ProjectMemberStatus[] {
    const statusIds = getMemberStatusIds(userId)
    if (!statusIds.length) {
      return []
    }
    const catalog = statuses.value
    return statusIds
      .map(statusId => catalog.find(item => item.id === statusId))
      .filter((item): item is ProjectMemberStatus => Boolean(item))
  }

  function addStatus(name: string): ProjectMemberStatus | null {
    const id = resolvedProjectId.value
    const trimmed = name.trim()
    if (!id || !trimmed) {
      return null
    }

    ensureProjectStatuses(id)

    const status: ProjectMemberStatus = {
      id: getUniqId(),
      name: trimmed,
    }

    statusesByProject.value[id] = [...statusesByProject.value[id], status]
    return status
  }

  function assignStatuses(userIds: string[], statusIds: string[]) {
    const id = resolvedProjectId.value
    if (!id || !userIds.length || !statusIds.length) {
      return
    }

    ensureProjectStatuses(id)

    const nextMemberMap = {
      ...memberStatusIdsByProject.value[id],
    }

    for (const userId of userIds) {
      const current = nextMemberMap[userId] ?? []
      const merged = Array.from(new Set([...current, ...statusIds]))
      nextMemberMap[userId] = merged
    }

    memberStatusIdsByProject.value = {
      ...memberStatusIdsByProject.value,
      [id]: nextMemberMap,
    }
  }

  return {
    statuses,
    getMemberStatusIds,
    getMemberStatuses,
    addStatus,
    assignStatuses,
  }
}
